(() => {
  const cleanups = new WeakMap();
  const accordionCleanups = new WeakMap();

  const initHero = (root) => {
    const hero = root.matches?.('[data-l-series-hero]') ? root : root.querySelector?.('[data-l-series-hero]');
    if (!hero || hero.dataset.lSeriesReady) return;
    hero.dataset.lSeriesReady = 'true';
    const media = hero.querySelector('[data-l-series-parallax-product]');
    const pointer = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
    let frame;
    hero.addEventListener('pointermove', (event) => {
      if (!pointer.matches || !media) return;
      const box = hero.getBoundingClientRect();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        media.style.setProperty('--l-series-parallax-x', `${((event.clientX - box.left) / box.width - .5) * 12}px`);
        media.style.setProperty('--l-series-parallax-y', `${((event.clientY - box.top) / box.height - .5) * 12}px`);
      });
    });
    hero.addEventListener('pointerleave', () => {
      media?.style.removeProperty('--l-series-parallax-x');
      media?.style.removeProperty('--l-series-parallax-y');
    });
  };

  const initAccordions = (root) => {
    const groups = root.matches?.('[data-l-series-accordion-group]') ? [root] : [...(root.querySelectorAll?.('[data-l-series-accordion-group]') || [])];
    groups.forEach((group) => {
      if (accordionCleanups.has(group)) return;
      const items = [...group.querySelectorAll('[data-l-series-accordion-item]')];
      const animations = new Map();
      const desiredStates = new Map();
      const listeners = [];
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const parts = (details) => {
        const summary = details.querySelector(':scope > summary');
        const panel = summary?.getAttribute('aria-controls') ? document.getElementById(summary.getAttribute('aria-controls')) : null;
        return { summary, panel };
      };
      const clearStyles = (panel) => {
        panel.style.removeProperty('height');
        panel.style.removeProperty('overflow');
        panel.style.removeProperty('opacity');
        panel.style.removeProperty('transform');
      };
      const finishImmediately = (details, shouldOpen) => {
        animations.get(details)?.cancel();
        animations.delete(details);
        const { summary, panel } = parts(details);
        details.open = shouldOpen;
        desiredStates.set(details, shouldOpen);
        summary?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        if (panel) clearStyles(panel);
      };
      const animate = (details, shouldOpen) => {
        const { summary, panel } = parts(details);
        if (!summary || !panel || reducedMotion.matches || typeof panel.animate !== 'function') {
          finishImmediately(details, shouldOpen);
          return;
        }

        const wasOpen = details.open;
        const previous = animations.get(details);
        const startHeight = panel.getBoundingClientRect().height;
        const current = getComputedStyle(panel);
        const startOpacity = wasOpen ? current.opacity : '0';
        const startTransform = wasOpen && current.transform !== 'none' ? current.transform : (wasOpen ? 'translateY(0)' : 'translateY(3px)');
        previous?.cancel();
        desiredStates.set(details, shouldOpen);
        summary.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        if (shouldOpen) details.open = true;

        panel.style.height = `${startHeight}px`;
        panel.style.overflow = 'hidden';
        panel.style.opacity = startOpacity;
        panel.style.transform = startTransform;
        const endHeight = shouldOpen ? panel.scrollHeight : 0;
        const animation = panel.animate([
          { height: `${startHeight}px`, opacity: startOpacity, transform: startTransform },
          { height: `${endHeight}px`, opacity: shouldOpen ? 1 : 0, transform: shouldOpen ? 'translateY(0)' : 'translateY(-3px)' }
        ], { duration: 360, easing: 'ease-in-out', fill: 'forwards' });
        animations.set(details, animation);
        animation.finished.then(() => {
          if (animations.get(details) !== animation) return;
          animations.delete(details);
          if (!shouldOpen) details.open = false;
          animation.cancel();
          clearStyles(panel);
        }).catch(() => {});
      };

      let hasOpenItem = false;
      items.forEach((details) => {
        const { summary } = parts(details);
        if (!summary) return;
        if (details.open && hasOpenItem) details.open = false;
        if (details.open) hasOpenItem = true;
        desiredStates.set(details, details.open);
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        const onToggle = () => {
          if (animations.has(details)) return;
          desiredStates.set(details, details.open);
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        };
        const onClick = (event) => {
          event.preventDefault();
          const shouldOpen = !(desiredStates.get(details) ?? details.open);
          if (shouldOpen) items.forEach((item) => {
            if (item !== details && (desiredStates.get(item) || item.open)) animate(item, false);
          });
          animate(details, shouldOpen);
        };
        details.addEventListener('toggle', onToggle);
        summary.addEventListener('click', onClick);
        listeners.push(() => details.removeEventListener('toggle', onToggle), () => summary.removeEventListener('click', onClick));
      });

      accordionCleanups.set(group, () => {
        listeners.forEach((remove) => remove());
        animations.forEach((animation, details) => {
          animation.cancel();
          const { panel } = parts(details);
          if (panel) clearStyles(panel);
        });
        animations.clear();
      });
    });
  };

  const initProduct = (root) => {
    const product = root.matches?.('[data-l-series-product]') ? root : root.querySelector?.('[data-l-series-product]');
    if (!product || product.dataset.lSeriesReady) return;
    product.dataset.lSeriesReady = 'true';
    const picker = product.querySelector('variant-radios');
    const variantDataNode = picker?.querySelector('script[type="application/json"]');
    const variants = variantDataNode ? JSON.parse(variantDataNode.textContent) : [];
    const summary = product.querySelector('[data-l-series-model-summary]');
    const modelContent = summary?.querySelector('[data-l-series-model-content]');
    const dataNode = document.querySelector('[data-l-series-model-data]');
    const modelData = dataNode ? JSON.parse(dataNode.textContent) : {};
    const rail = product.querySelector('[data-l-series-purchase-rail]');
    const nativePrice = product.querySelector('[id^="price-"]');
    const nativeSubmit = product.querySelector('.product-form__submit[name="add"]');
    const railPrice = rail?.querySelector('[data-l-series-rail-price]');
    const railSubmit = rail?.querySelector('[data-l-series-rail-submit]');
    const railLabel = rail?.querySelector('[data-l-series-rail-label]');
    const railModel = rail?.querySelector('[data-l-series-rail-model]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let modelTransition = 0;
    let modelAnimations = [];

    const selectedModel = () => [...(picker?.querySelectorAll('fieldset') || [])].map((field) => field.querySelector('input:checked')?.value).filter(Boolean).join(' / ');
    const selectedVariant = () => {
      const options = [...(picker?.querySelectorAll('fieldset') || [])].map((field) => field.querySelector('input:checked')?.value);
      return variants.find((variant) => variant.options.every((option, index) => option === options[index]));
    };
    const activateVariantMedia = () => {
      const variant = selectedVariant();
      const mediaId = variant?.featured_media?.id;
      if (!mediaId || !picker?.dataset.section) return;

      const gallery = document.getElementById(`MediaGallery-${picker.dataset.section}`);
      const target = `${picker.dataset.section}-${mediaId}`;
      const media = gallery?.querySelector(`[data-media-id="${target}"]`);
      const thumbnail = gallery?.querySelector(`[data-target="${target}"]`);
      if (!gallery || typeof gallery.setActiveMedia !== 'function' || !media || !thumbnail) return;

      const mediaIsActive = media.classList.contains('is-active');
      const thumbnailIsActive = thumbnail.querySelector('button')?.getAttribute('aria-current') === 'true';
      if (mediaIsActive && thumbnailIsActive) return;
      gallery.setActiveMedia(target, true);
    };
    const modelMarkup = (details) => {
      const components = details.included.map((component) => `<li><span class="aqua-l-series-product__model-check" aria-hidden="true"><svg viewBox="0 0 16 16" focusable="false"><path d="m3.25 8.15 2.85 2.8 6.65-6.4"/></svg></span><span>${component}</span></li>`).join('');
      return `<p class="aqua-l-series-product__model-title">${details.positioning}</p><p class="aqua-l-series-product__model-description">${details.summary}</p><p class="aqua-l-series-product__model-eyebrow">What's included</p><ul class="aqua-l-series-product__model-components">${components}</ul>`;
    };
    const cancelModelAnimations = () => {
      modelAnimations.forEach((animation) => animation.cancel());
      modelAnimations = [];
    };
    const syncModel = async (animate = false) => {
      const model = selectedModel();
      const details = modelData[model];
      const variant = selectedVariant();
      if (railModel && variant?.title) railModel.textContent = `AQUA-${variant.title} Kit`;
      if (!modelContent) return;
      const markup = details ? modelMarkup(details) : '';
      const transition = ++modelTransition;
      const currentStyle = getComputedStyle(modelContent);
      const currentOpacity = currentStyle.opacity;
      const currentTransform = currentStyle.transform === 'none' ? 'translateY(0)' : currentStyle.transform;
      cancelModelAnimations();
      if (!animate || reducedMotion.matches || !modelContent.animate) {
        modelContent.innerHTML = markup;
        modelContent.style.removeProperty('opacity');
        modelContent.style.removeProperty('transform');
        return;
      }

      modelContent.style.opacity = currentOpacity;
      modelContent.style.transform = currentTransform;
      const exit = modelContent.animate([
        { opacity: currentOpacity, transform: currentTransform },
        { opacity: 0, transform: 'translateY(-3px)' }
      ], { duration: 140, easing: 'ease-out', fill: 'forwards' });
      modelAnimations = [exit];
      try { await exit.finished; } catch (error) { return; }
      if (transition !== modelTransition) return;

      exit.cancel();
      modelAnimations = [];
      modelContent.innerHTML = markup;
      modelContent.style.opacity = '0';
      modelContent.style.transform = 'translateY(3px)';
      const reveal = modelContent.animate([
        { opacity: 0, transform: 'translateY(3px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 220, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'forwards' });
      modelAnimations = [reveal];
      try { await Promise.all(modelAnimations.map((animation) => animation.finished)); } catch (error) { return; }
      if (transition !== modelTransition) return;
      modelAnimations.forEach((animation) => animation.cancel());
      modelAnimations = [];
      modelContent.style.removeProperty('opacity');
      modelContent.style.removeProperty('transform');
    };
    const syncRail = () => {
      if (!rail || !nativeSubmit || !railSubmit) return;
      if (nativePrice && railPrice) railPrice.innerHTML = nativePrice.innerHTML;
      railSubmit.disabled = nativeSubmit.disabled || nativeSubmit.getAttribute('aria-disabled') === 'true';
      const label = nativeSubmit.querySelector('span')?.textContent.trim();
      if (label && railLabel) railLabel.textContent = !railSubmit.disabled && label === railSubmit.dataset.lSeriesNativeLabel ? railSubmit.dataset.lSeriesButtonLabel : label;
    };
    syncModel();
    syncRail();
    customElements.whenDefined('media-gallery').then(activateVariantMedia);
    const handleModelChange = () => {
      syncModel(true);
      activateVariantMedia();
    };
    picker?.addEventListener('change', handleModelChange);
    if (!rail || !nativeSubmit || !railSubmit) return;
    rail.hidden = false;
    railSubmit.addEventListener('click', () => { syncRail(); if (!railSubmit.disabled) nativeSubmit.click(); });
    const mutation = new MutationObserver(syncRail);
    if (nativePrice) mutation.observe(nativePrice, { childList: true, subtree: true, characterData: true });
    mutation.observe(nativeSubmit, { attributes: true, childList: true, subtree: true });
const buttonObserver = new IntersectionObserver(([entry]) => {
  const hasPassedNativeButton =
    !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;

  rail.classList.toggle('is-visible', hasPassedNativeButton);
  rail.classList.toggle('is-native-visible', entry.isIntersecting);

  document.body.classList.toggle(
    'l-series-purchase-rail-visible',
    hasPassedNativeButton
  );

  if (hasPassedNativeButton) {
    document.body.style.setProperty(
      '--l-series-sticky-bar-offset',
      `${rail.getBoundingClientRect().height}px`
    );
  } else {
    document.body.style.removeProperty('--l-series-sticky-bar-offset');
  }
}, { threshold: 0 });

buttonObserver.observe(nativeSubmit);
    cleanups.set(product, () => {
      modelTransition += 1; cancelModelAnimations(); picker?.removeEventListener('change', handleModelChange);
      modelContent?.style.removeProperty('opacity'); modelContent?.style.removeProperty('transform');
      mutation.disconnect(); buttonObserver.disconnect();
      document.body.classList.remove('l-series-purchase-rail-visible');
      document.body.style.removeProperty('--l-series-sticky-bar-offset');
    });
  };

  const init = (root = document) => { initHero(root); initProduct(root); initAccordions(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const product = event.target.querySelector?.('[data-l-series-product]');
    if (product) cleanups.get(product)?.();
    const accordionGroup = event.target.querySelector?.('[data-l-series-accordion-group]');
    if (accordionGroup) accordionCleanups.get(accordionGroup)?.();
  });
})();
